import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Business,
  Person,
  LocationOn,
  CalendarToday,
  Add,
  Check,
  Search,
  Email,
  Phone,
} from '@mui/icons-material';
import CreditAnalysisConfirmDialog from '../../../components/CreditAnalysisConfirmDialog/CreditAnalysisConfirmDialog';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import * as quoteService from '../../../services/quote-service';
import { formatPhone } from 'utils/formatters';

// Define interfaces to match the backend response
interface Reseller {
  id: string;
  name: string;
  email: string;
  phone?: string;
}

interface ShippingAddress {
  street: string;
  number: string | null;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  is_default: boolean;
  complement: string | null;
  is_billing: boolean;
  is_delivery: boolean;
  billing: boolean;
  id: string;
  customer_id: string;
}

interface Customer {
  company_name: string;
  phone: string;
  email: string;
  cnpj: string;
  sector: string;
  state_registration: string;
  id: string;
  reseller_id: string;
  created_at: string;
  reseller: {
    id: string;
    name: string;
    email: string;
    phone?: string;
  };
}

interface Quote {
  reseller_id: string;
  customer_id: string;
  payment_method: string;
  payment_term: string;
  shipping_address_id: string;
  status: string;
  expiration_date: string;
  id: string;
  code: string;
  customer: Customer;
  reseller: Reseller;
  shipping_address: ShippingAddress;
  created_at: string;
  total_estimated: number;
}

interface ApiResponse {
  items: Quote[];
  total: number;
}

interface User {
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  is_approved: boolean;
  id: string;
  created_at: string;
}

interface UsersResponse {
  items: User[];
  total: number;
}

interface QuoteCreationResponse {
  id: string;
  code: string;
  status: string;
  created_at: string;
}

const AdminQuotesPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [openCustomersModal, setOpenCustomersModal] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedReseller, setSelectedReseller] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchTimeout, setCustomerSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [selectedQuoteForConversion, setSelectedQuoteForConversion] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Cotações', path: '/admin/quotes' },
    ]);
  }, [setBreadcrumbs]);

  // Função para buscar cotações
  const fetchQuotes = async () => {
    setLoading(true);
    try {
      // Direct API call to match your backend format
      const url = `/admin/quotes/?skip=${page * rowsPerPage}&limit=${rowsPerPage}`;
      console.log('Fetching quotes from URL:', url);

      const data = await api.get<ApiResponse>(url);
      console.log('API response data:', data);

      // Check if data is valid and contains items array
      if (data && Array.isArray(data.items)) {
        console.log('Quotes array length:', data.items.length);
        setQuotes(data.items);
        setTotalItems(data.total || data.items.length);
      } else {
        console.error('Invalid data format or missing items array:', data);
        setQuotes([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      enqueueSnackbar('Falha ao carregar cotações', { variant: 'error' });
      setQuotes([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Função para abrir o dialog de confirmação de análise de crédito
  const handleConvertToOrder = async (quoteId: string) => {
    setSelectedQuoteForConversion(quoteId);
    setCreditDialogOpen(true);
  };

  // Função para processar a conversão após confirmação
  const handleCreditAnalysisConfirm = async (verifyCredit: boolean) => {
    if (!selectedQuoteForConversion) return;

    try {
      setConverting(true);
      setCreditDialogOpen(false);

      // Mostrar mensagem de processamento
      enqueueSnackbar('Processando conversão...', { variant: 'info' });

      // Chamar o serviço para converter a cotação em pedido
      const response = await quoteService.convertQuoteToOrder(
        selectedQuoteForConversion,
        verifyCredit
      );

      if (response.success) {
        // Exibir mensagem de sucesso
        enqueueSnackbar('Cotação convertida em pedido com sucesso!', { variant: 'success' });

        // Recarregar a lista de cotações
        fetchQuotes();
      } else {
        // Exibir mensagem de erro
        enqueueSnackbar(response.message || 'Erro ao converter cotação em pedido', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Erro ao converter cotação:', error);
      enqueueSnackbar('Erro ao converter cotação em pedido', { variant: 'error' });
    } finally {
      setConverting(false);
      setSelectedQuoteForConversion(null);
    }
  };

  // Função para fechar o dialog de confirmação
  const handleCreditDialogClose = () => {
    setCreditDialogOpen(false);
    setSelectedQuoteForConversion(null);
  };

  // Função para excluir cotação
  const handleDeleteQuote = async (quoteId: string) => {
    try {
      // Chamada da API para excluir a cotação
      await api.del(`/admin/quotes/${quoteId}`);

      // Exibir mensagem de sucesso
      enqueueSnackbar('Cotação excluída com sucesso.', { variant: 'success' });

      // Recarregar a lista de cotações
      fetchQuotes();
    } catch (error) {
      console.error('Erro ao excluir cotação:', error);
      enqueueSnackbar('Erro ao excluir cotação', { variant: 'error' });
    }
  };

  // Debug quotes state
  console.log('Current quotes state:', quotes);
  console.log('Quotes array length:', quotes?.length || 0);

  const getStatusChipColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'primary';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'draft':
        return 'default';
      case 'analise de credito':
        return 'warning';
      default:
        return 'info';
    }
  };

  // Format date in a user-friendly way
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  // Get address summary
  const getAddressSummary = (address: ShippingAddress) => {
    if (!address) return 'Endereço não fornecido';
    return `${address.city}, ${address.state}, ${address.country}`;
  };

  // Translate payment method for display
  const translatePaymentMethod = (method: string): string => {
    switch (method) {
      case 'Partial Credit':
        return 'Crédito Parcial';
      case 'Bank Slip':
        return 'Boleto Bancário';
      default:
        return method;
    }
  };

  const handleOpenModal = async () => {
    setOpenModal(true);
    await fetchUsers();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setUsersPage(0);
    setUsersRowsPerPage(10);
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
      const response = await api.get<UsersResponse>(
        `/admin/users/?skip=${usersPage * usersRowsPerPage}&limit=${usersRowsPerPage}&sort=name:asc${searchParam}`
      );
      const filteredUsers = response.items.filter(user => user.role !== 'admin');
      setUsers(filteredUsers);
      setTotalUsers(response.total);
    } catch (error) {
      console.error('Error fetching users:', error);
      enqueueSnackbar('Falha ao carregar revendedores', { variant: 'error' });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUsersPageChange = (_event: unknown, newPage: number) => {
    setUsersPage(newPage);
  };

  const handleUsersRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsersRowsPerPage(parseInt(event.target.value, 10));
    setUsersPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout to debounce search
    const timeout = setTimeout(() => {
      setUsersPage(0); // Reset to first page when searching
      fetchUsers();
    }, 500);

    setSearchTimeout(timeout);
  };

  useEffect(() => {
    if (openModal) {
      fetchUsers();
    }
  }, [usersPage, usersRowsPerPage, openModal]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleSelectReseller = async (userId: string) => {
    const reseller = users.find(user => user.id === userId);
    if (reseller) {
      setSelectedReseller(reseller);
      setOpenCustomersModal(true);
      await fetchCustomers(userId);
    }
  };

  const handleCloseCustomersModal = () => {
    setOpenCustomersModal(false);
    setSelectedReseller(null);
    setCustomers([]);
    setCustomerSearchQuery(''); // Reset search query when closing modal
  };

  const fetchCustomers = async (userId: string) => {
    setLoadingCustomers(true);
    try {
      const searchParam = customerSearchQuery
        ? `?search=${encodeURIComponent(customerSearchQuery)}`
        : '';
      const response = await api.get<Customer[]>(`/admin/users/${userId}/customers${searchParam}`);
      setCustomers(response);
    } catch (error) {
      console.error('Error fetching customers:', error);
      enqueueSnackbar('Falha ao carregar clientes', { variant: 'error' });
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleCustomerSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCustomerSearchQuery(value);

    // Clear previous timeout
    if (customerSearchTimeout) {
      clearTimeout(customerSearchTimeout);
    }

    // Set new timeout to debounce search
    const timeout = setTimeout(() => {
      if (selectedReseller) {
        fetchCustomers(selectedReseller.id);
      }
    }, 500);

    setCustomerSearchTimeout(timeout);
  };

  // Cleanup customer search timeout on unmount
  useEffect(() => {
    return () => {
      if (customerSearchTimeout) {
        clearTimeout(customerSearchTimeout);
      }
    };
  }, [customerSearchTimeout]);

  const handleSelectCustomer = async (customerId: string) => {
    setCreatingQuote(true);
    try {
      const quoteData = {
        customer_id: customerId,
        payment_method: 'BankSlip',
        payment_term: '30',
        shipping_address_id: '', // This will be handled by the backend
        status: 'Approved',
        expiration_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0], // 30 days from now
        reseller_id: selectedReseller?.id,
        items: [],
      };

      const response = await api.post<QuoteCreationResponse, any>('/admin/quotes/', quoteData);

      if (response) {
        enqueueSnackbar('Cotação criada com sucesso!', { variant: 'success' });
        handleCloseCustomersModal();
        handleCloseModal();
        // Navigate to the quote details page
        navigate(`/admin/quotes/${response.id}`);
      }
    } catch (error) {
      console.error('Error creating quote:', error);
      enqueueSnackbar('Erro ao criar cotação', { variant: 'error' });
    } finally {
      setCreatingQuote(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenModal}>
          Adicionar Cotação
        </Button>
      </Box>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Selecione um Revendedor</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Pesquisar por nome ou e-mail"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {loadingUsers ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell align="right">
                          <IconButton color="primary" onClick={() => handleSelectReseller(user.id)}>
                            <Check />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalUsers}
                page={usersPage}
                onPageChange={handleUsersPageChange}
                rowsPerPage={usersRowsPerPage}
                onRowsPerPageChange={handleUsersRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openCustomersModal} onClose={handleCloseCustomersModal} maxWidth="md" fullWidth>
        <DialogTitle>Clientes do Revendedor: {selectedReseller?.name}</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Pesquisar por nome ou CNPJ"
              value={customerSearchQuery}
              onChange={handleCustomerSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {loadingCustomers || creatingQuote ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>CNPJ</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.length > 0 ? (
                    customers.map(customer => (
                      <TableRow key={customer.id}>
                        <TableCell>{customer.company_name}</TableCell>
                        <TableCell>{customer.cnpj}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={() => handleSelectCustomer(customer.id)}
                            disabled={creatingQuote}
                          >
                            <Check />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        Nenhum cliente encontrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Código da Cotação</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Revendedor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Pagamento</TableCell>
                  <TableCell>Total Estimado (R$)</TableCell>
                  <TableCell>Datas</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotes && quotes.length > 0 ? (
                  quotes.map(quote => (
                    <TableRow
                      key={quote.id}
                      hover
                      component={Link}
                      to={`/admin/quotes/${quote.id}`}
                      sx={{
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography
                          style={{ textDecoration: 'none', color: 'inherit', fontWeight: 'bold' }}
                        >
                          {quote.code}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="column" spacing={1}>
                          <Tooltip title={quote.customer?.cnpj || ''}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Business fontSize="small" color="action" />
                              <Typography variant="body2">
                                {quote.customer?.company_name || 'Cliente desconhecido'}
                              </Typography>
                            </Stack>
                          </Tooltip>
                          <Tooltip title={getAddressSummary(quote.shipping_address)}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LocationOn fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {quote.shipping_address?.city}, {quote.shipping_address?.state}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="column" spacing={1}>
                          <Tooltip title={quote.reseller?.name || 'Revendedor desconhecido'}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Person fontSize="small" color="action" />
                              <Typography variant="body2">
                                {quote.reseller?.name || 'Revendedor desconhecido'}
                              </Typography>
                            </Stack>
                          </Tooltip>
                          <Tooltip title={quote.reseller?.email || ''}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Email fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {quote.reseller?.email || ''}
                              </Typography>
                            </Stack>
                          </Tooltip>
                          <Tooltip
                            title={quote.reseller?.phone ? formatPhone(quote.reseller?.phone) : ''}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Phone fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {quote.reseller?.phone ? formatPhone(quote.reseller?.phone) : ''}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={quote.status}
                          size="small"
                          color={getStatusChipColor(quote.status) as any}
                        />
                      </TableCell>

                      <TableCell>
                        <Stack direction="column" spacing={0.5}>
                          <Typography variant="body2">
                            {translatePaymentMethod(quote.payment_method)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {quote.payment_term} dias
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {quote.total_estimated
                            ? `R$ ${quote.total_estimated.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : 'N/A'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="column" spacing={0.5}>
                          <Tooltip title="Data de Criação">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CalendarToday fontSize="small" color="action" />
                              <Typography variant="caption">
                                {formatDate(quote.created_at)}
                              </Typography>
                            </Stack>
                          </Tooltip>
                          <Tooltip title="Data de Expiração">
                            <Typography variant="caption" color="text.secondary">
                              Expira em: {formatDate(quote.expiration_date)}
                            </Typography>
                          </Tooltip>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {(quote.status === 'Analise de Credito' ||
                            quote.status === 'Em Análise') && (
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleConvertToOrder(quote.id);
                              }}
                            >
                              Transformar em Pedido
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            size="small"
                            color="error"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (window.confirm('Tem certeza que deseja excluir esta cotação?')) {
                                handleDeleteQuote(quote.id);
                              }
                            }}
                          >
                            Excluir
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhuma cotação encontrada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}

      {/* Dialog de confirmação para análise de crédito */}
      <CreditAnalysisConfirmDialog
        open={creditDialogOpen}
        onClose={handleCreditDialogClose}
        onConfirm={handleCreditAnalysisConfirm}
        loading={converting}
      />
    </Box>
  );
};

export default AdminQuotesPage;
